// src/App.js
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  ProtectedLayout,
  ToastContainer,
  DateProvider,
  PendingHandoffAlert,
  DailyBankBalanceAlert,
  PendingTransferAlert,
  PermissionsChangedAlert,
} from "@common";
import { PublicRoute, ProtectedRoute } from "@components";
import { useAuth } from "@api/useAuth";
import { CLINIC_REPORT_ROLES, ROLE_IDS, getDefaultRouteForUser } from "@utils";
import {
  Login,
  Register,
  Password,
  ForgotPassword,
  ResetPassword,
  AccountStatus,
  Admin,
  Management,
  Scheduling,
  Profile,
  ClinicReport,
  SchedulePage,
  ApprovalsPage,
  VacationRequestPage,
  NewShiftPage,
  Report,
  CashManagement,
  AccessClinicCashFlow,
  DensNDenteCashFlow,
  FinanceOperationsPage,
} from "@pages";

function RoleRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user && user.status && user.status !== "active") {
    return <Navigate to="/account-status" replace />;
  }
  return <Navigate to={getDefaultRouteForUser(user)} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <DateProvider>
        <ToastContainer />
        <PendingHandoffAlert />
        <PendingTransferAlert />
        <DailyBankBalanceAlert />
        <PermissionsChangedAlert />

        <Routes>
          {/* Public - Redirect to dashboard if already logged in */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/password"
            element={
              <PublicRoute>
                <Password />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          {/* Account Status - accessible by logged-in users regardless of status */}
          <Route
            path="/account-status"
            element={
              <ProtectedRoute skipStatusCheck>
                <AccountStatus />
              </ProtectedRoute>
            }
          />

          {/* Protected (header shown for all children) */}
          {/* Admin & Scheduling - Organizer, Admin, Observer (read-only) */}
          <Route
            element={
              <ProtectedLayout roles={[ROLE_IDS.ORGANIZER, ROLE_IDS.ADMIN, ROLE_IDS.OBSERVER]} />
            }
          >
            <Route path="/admin" element={<Admin />} />
            <Route path="/scheduling" element={<Scheduling />} />
          </Route>

          {/* Cash Flow (clinic-grouped) + Dens N Dente Cash Flow (bank-account-grouped)
              - Organizer, Admin, Observer (read-only), Payroll, Bookkeeper, Finance Coordinator */}
          <Route
            element={
              <ProtectedLayout
                roles={[
                  ROLE_IDS.ORGANIZER,
                  ROLE_IDS.ADMIN,
                  ROLE_IDS.OBSERVER,
                  ROLE_IDS.PAYROLL,
                  ROLE_IDS.BOOKKEEPER,
                  ROLE_IDS.FINANCE_COORDINATOR,
                ]}
              />
            }
          >
            <Route
              path="/cash-flow"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE_IDS.ORGANIZER,
                    ROLE_IDS.ADMIN,
                    ROLE_IDS.OBSERVER,
                    ROLE_IDS.PAYROLL,
                    ROLE_IDS.BOOKKEEPER,
                    ROLE_IDS.FINANCE_COORDINATOR,
                  ]}
                >
                  <AccessClinicCashFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dens-n-dente-cash-flow"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE_IDS.ORGANIZER,
                    ROLE_IDS.ADMIN,
                    ROLE_IDS.OBSERVER,
                    ROLE_IDS.PAYROLL,
                    ROLE_IDS.BOOKKEEPER,
                    ROLE_IDS.FINANCE_COORDINATOR,
                  ]}
                >
                  <DensNDenteCashFlow />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Finance Operations - everyone except Visitor (Practitioner Lead:
              Daily Deposits only) */}
          <Route
            element={
              <ProtectedLayout
                roles={[
                  ROLE_IDS.ORGANIZER,
                  ROLE_IDS.ADMIN,
                  ROLE_IDS.AREA_MANAGER,
                  ROLE_IDS.PRACTITIONER_LEAD,
                  ROLE_IDS.OBSERVER,
                  ROLE_IDS.PAYROLL,
                  ROLE_IDS.BOOKKEEPER,
                  ROLE_IDS.FINANCE_COORDINATOR,
                ]}
              />
            }
          >
            <Route path="/finance-operations" element={<FinanceOperationsPage />} />
          </Route>

          {/* Management - Area Manager and Bookkeeper (read-only) */}
          <Route element={<ProtectedLayout roles={[ROLE_IDS.AREA_MANAGER, ROLE_IDS.BOOKKEEPER]} />}>
            <Route path="/management" element={<Management />} />
          </Route>

          {/* Report landing page - For roles that have any clinic visibility */}
          <Route
            element={
              <ProtectedLayout
                roles={[
                  ROLE_IDS.ORGANIZER,
                  ROLE_IDS.ADMIN,
                  ROLE_IDS.AREA_MANAGER,
                  ROLE_IDS.PRACTITIONER_LEAD,
                  ROLE_IDS.VISITOR,
                  ROLE_IDS.OBSERVER,
                  ROLE_IDS.BOOKKEEPER,
                ]}
              />
            }
          >
            <Route path="/profile" element={<Profile />} />
            <Route path="/report" element={<Report />} />
          </Route>

          {/* Clinic Reports - Roles that can view a specific clinic */}
          <Route element={<ProtectedLayout roles={CLINIC_REPORT_ROLES} />}>
            <Route path="/report/:clinicId" element={<ClinicReport />} />
            <Route path="/report/:clinicId/schedule" element={<SchedulePage />} />
            <Route path="/report/:clinicId/schedule/approvals" element={<ApprovalsPage />} />
            <Route
              path="/report/:clinicId/schedule/vacation-request"
              element={<VacationRequestPage />}
            />
            <Route path="/report/:clinicId/schedule/new-shift" element={<NewShiftPage />} />
          </Route>

          {/* Cash Management - any role with clinic access */}
          <Route
            element={
              <ProtectedLayout
                roles={[
                  ROLE_IDS.ORGANIZER,
                  ROLE_IDS.ADMIN,
                  ROLE_IDS.AREA_MANAGER,
                  ROLE_IDS.PRACTITIONER_LEAD,
                  ROLE_IDS.VISITOR,
                  ROLE_IDS.OBSERVER,
                  ROLE_IDS.PAYROLL,
                  ROLE_IDS.BOOKKEEPER,
                ]}
              />
            }
          >
            <Route path="/cash-management" element={<CashManagement />} />
          </Route>

          {/* Redirects */}
          <Route path="/" element={<RoleRedirect />} />
          <Route path="*" element={<RoleRedirect />} />
        </Routes>
      </DateProvider>
    </BrowserRouter>
  );
}
